import React from 'react';
import { Box, Typography, useTheme } from '@mui/material';
import {
  Inbox as InboxIcon,
  Refresh as RefreshIcon,
  Email as EmailIcon,
  CheckCircle as CheckCircleIcon,
} from '@mui/icons-material';

interface Step {
  label: string;
  icon: React.ReactNode;
  status: 'completed' | 'current' | 'future';
}

interface StepProgressProps {
  steps: Step[];
}

const StepProgress: React.FC<StepProgressProps> = ({ steps }) => {
  const theme = useTheme();

  const getStepColor = (status: Step['status']) => {
    switch (status) {
      case 'completed':
        return theme.palette.success.main;
      case 'current':
        return theme.palette.primary.main;
      case 'future':
        return theme.palette.grey[400];
    }
  };

  const getBackgroundColor = (status: Step['status']) => {
    switch (status) {
      case 'completed':
        return theme.palette.success.light;
      case 'current':
        return theme.palette.primary.light;
      case 'future':
        return theme.palette.grey[100];
    }
  };

  return (
    <Box sx={{ width: '100%', py: 3 }}>
      <Box
        sx={{
          display: 'flex',
          justifyContent: 'space-between',
          alignItems: 'stretch',
          position: 'relative',
        }}
      >
        {/* Ligne de connexion entre les étapes */}
        <Box
          sx={{
            position: 'absolute',
            top: '32px',
            left: '10%',
            right: '10%',
            height: '4px',
            bgcolor: theme.palette.grey[300],
            zIndex: 0,
          }}
        />

        {steps.map((step, index) => (
          <Box
            key={index}
            sx={{
              flex: 1,
              display: 'flex',
              flexDirection: 'column',
              alignItems: 'center',
              position: 'relative',
              zIndex: 1,
            }}
          >
            {/* Icône de l'étape */}
            <Box
              sx={{
                width: 64,
                height: 64,
                borderRadius: '50%',
                bgcolor: getBackgroundColor(step.status),
                border: `4px solid ${getStepColor(step.status)}`,
                display: 'flex',
                alignItems: 'center',
                justifyContent: 'center',
                mb: 2,
                transition: 'all 0.3s ease',
                ...(step.status === 'current' && {
                  boxShadow: `0 0 0 4px ${theme.palette.primary.light}`,
                  transform: 'scale(1.1)',
                }),
              }}
            >
              <Box sx={{ color: getStepColor(step.status), fontSize: 28 }}>
                {step.icon}
              </Box>
            </Box>

            {/* Label de l'étape */}
            <Typography
              variant="body2"
              sx={{
                textAlign: 'center',
                fontWeight: step.status === 'current' ? 600 : 400,
                color: step.status === 'future' ? theme.palette.text.secondary : theme.palette.text.primary,
                maxWidth: 120,
              }}
            >
              {step.label}
            </Typography>

            {/* Indicateur de progression */}
            {step.status === 'completed' && (
              <Box
                sx={{
                  position: 'absolute',
                  top: 20,
                  right: 20,
                  bgcolor: theme.palette.success.main,
                  borderRadius: '50%',
                  width: 24,
                  height: 24,
                  display: 'flex',
                  alignItems: 'center',
                  justifyContent: 'center',
                }}
              >
                <CheckCircleIcon sx={{ color: 'white', fontSize: 16 }} />
              </Box>
            )}
          </Box>
        ))}
      </Box>
    </Box>
  );
};

export default StepProgress;
